import React, {useContext, useMemo} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {TForm} from '@src/components/_views/configurable/import-view/file-list/.types';
import CustomSelect from '@src/components/Fields/CustomSelect';
import {IImportConfig} from '@src/@interfaces/components/Import/IImportConfig';
import {FormFieldOption} from '@src/@types/components/Form';
import {CustomSelectProps} from '@src/@types/components/Fields/CustomSelect';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';

type TOption = FormFieldOption & { title: string };

export default function ColumnField(props: {
    rowIndex: number,
    columnIndex: number,
    config: IImportConfig,
}) {
    const {rowIndex, columnIndex, config} = props;
    const {locale} = useContext(LocaleContext);
    const {control} = useFormContext<TForm>();
    const {append, update, remove} = useFieldArray({control, name: 'columns', keyName: 'uniqueKey'});

    const fields = useWatch({control, name: 'columns'});

    const {index, options, onChange, valueResolverByTitle} = useMemo(() => {
        const usedFields: Record<string, number> = {};
        const index = fields.findIndex((item, i) => {
            usedFields[item.fieldTitle] = i;

            return item.rowIndex === rowIndex && item.columnIndex === columnIndex;
        });

        const options = config
                .fields
                .reduce<TOption[]>((res, item) => {
                    const i = usedFields[item.title] ?? null;
                    if (item.multiple || i == null || i === index) {
                        res.push({
                            label: trans(
                                    item.label ?? config.fieldsLabelL10NPrefix + item.title,
                                    locale
                            ),
                            value: item.name,
                            title: item.title,
                        });
                    }

                    return res;
                }, []);

        const onChange: CustomSelectProps<false, TOption>['onChange'] = (value, option) => {
            if (!value && index === -1) {
                return;
            }

            if (!value) {
                return void remove(index);
            }

            if (index === -1) {
                return void append({
                    field: String(value),
                    fieldTitle: option.title,
                    columnIndex: columnIndex,
                    rowIndex: rowIndex,
                });
            }

            return void update(index, {
                field: String(value),
                fieldTitle: option.title,
                columnIndex: columnIndex,
                rowIndex: rowIndex,
            });
        };

        const valueResolverByTitle: CustomSelectProps<false, TOption>['valueResolver'] = (title, options) => {
            return options.find(option => option.title === title);
        };

        return {index, options, onChange, valueResolverByTitle};
    }, [append, columnIndex, config.fields, config.fieldsLabelL10NPrefix, fields, locale, remove, rowIndex, update]);

    return <CustomSelect<false, TOption>
            label="List:ImportRecords.item-config.fields.column"
            value={fields[index]?.fieldTitle ?? null}
            valueResolver={valueResolverByTitle}
            onChange={onChange}
            options={options}
            menuPortalTarget={document?.body}
            clearable
    />;
}
